<script lang="ts" setup>
import { usePreviewImg } from "@/use/common";
import { PickerConfirmEventParams, showFailToast } from "vant";
import { ref, computed } from "vue";
import { useGenStyle } from "@/stores/genStyle";
import CheckCircleFillIcon from "@/components/icon/CheckCircleFillIcon.vue";
import { useI18n } from "vue-i18n";
import { PropType } from "vue";
import { onMounted } from "vue";
import { useRoute } from "vue-router";
import { promptListRemoveTag } from "@/modules/common";

const props = defineProps({
  imageStyle: {
    type: Number,
  },
  mixStyle: {
    type: Array as PropType<MixStyle[]>,
  },
  customStyle: {
    type: Object as PropType<{
      version: number;
      prompt: string;
    }>,
  },
});

const emit = defineEmits([
  "update:mixStyle",
  "update:customStyle",
  "update:style",
]);

const genStyle = useGenStyle();
const { t } = useI18n();
const route = useRoute();
const mixStyle = computed({
  get: () => props.mixStyle || [],
  set: (val: MixStyle[]) => emit("update:mixStyle", val),
});

const clickPresetItem = (id: number) => {
  if (mixStyle.value.findIndex((item) => item.id === id) !== -1) {
    mixStyle.value = mixStyle.value.filter((item) => item.id !== id);
    return;
  }
  if (mixStyle.value.length === 0 || mixStyle.value[0].id === 0) {
    mixStyle.value = [{ id, percent: 1 }];
  } else if (mixStyle.value.length === 1) {
    mixStyle.value[0].percent = 0.5;
    mixStyle.value.push({ id, percent: 0.5 });
  } else return showFailToast(t(`painting.style.must2`));
};

// 融合设置
const showFuseSettingModal = ref(false);
const clickFuseSetting = () => {
  showFuseSettingModal.value = true;
  if (mixStyle.value.length === 2) {
    sliderVal.value = mixStyle.value[0].percent * 100;
  }
};
// 自定义标签
const option = ref([
  { text: t(`trait.mode.0`), value: 0 },
  { text: t(`trait.mode.1`), value: 1 },
  { text: t(`trait.mode.2`), value: 2 },
]);
const showOption = ref(true);
const versionText = computed(() =>
  t(`trait.mode.${props.customStyle!.version}`)
);

const showModelSelectModal = ref(false);
const onConfirm = (value: PickerConfirmEventParams) => {
  props.customStyle!.version = value.selectedValues[0] as number;
  showModelSelectModal.value = false;
};

const clickConfirmMix = () => {
  const [a, b] = mixStyle.value;
  if (a.percent + b.percent !== 1)
    return showFailToast(t(`painting.style.mixSettingError`));
  emit("update:mixStyle", mixStyle.value);
  showFuseSettingModal.value = false;
};

const sliderVal = ref(50);
const onChange = (value: number) => {
  let left = value;
  let right = 100 - value;
  mixStyle.value[0].percent = left / 100;
  mixStyle.value[1].percent = right / 100;
};

const onLeftChange = () => {
  const left = mixStyle.value[0].percent;
  if (left < 0 || left > 1) return;
  const right = 1 - left;
  mixStyle.value[1].percent = right;
  sliderVal.value = left * 100;
};

const onRightChange = () => {
  const right = mixStyle.value[1].percent;
  if (right < 0 || right > 1) return;
  const left = 1 - right;
  mixStyle.value[0].percent = left;
  sliderVal.value = left * 100;
};
const clickSwap = () => {
  const [a, b] = mixStyle.value;
  [a.id, b.id] = [b.id, a.id];
  mixStyle.value = [a, b];
};

const clickCustomItem = (name: string) => {
  if (props.customStyle?.prompt.includes(name)) {
    props.customStyle.prompt = promptListRemoveTag(
      props.customStyle.prompt,
      name
    );
  } else {
    props.customStyle!.prompt += "," + name;
  }
};

onMounted(() => {
  const { imageStyle } = props;
  if (props.mixStyle?.length === 0 && imageStyle)
    mixStyle.value = [{ id: imageStyle, percent: 1 }];
  if ([6, 9].includes(Number(route.query.mode))) showOption.value = false;
  if ([2, 3, 4].includes(Number(route.query.mode))) {
    option.value = [
      { text: t(`trait.mode.1`), value: 1 },
      { text: t(`trait.mode.2`), value: 2 },
    ];
    if (props.customStyle?.version === 0) props.customStyle!.version = 1;
  }
});
</script>

<template>
  <!-- 平台预设/自定义标签选取 -->
  <div class="overflow-hidden h-full flex flex-col border-t">
    <div class="mx-2">
      <!-- 类型 -->
      <div v-if="showOption">
        <div class="flex items-center justify-between mt-3 mb-2">
          <h2 class="text-base font-bold">
            {{ $t("painting.style.modelVersion") }}
          </h2>
        </div>

        <VanField
          v-model="versionText"
          :placeholder="$t('game.typePlaceholder')"
          class="border rounded"
          is-link
          name="picker"
          readonly
          required
          @click="showModelSelectModal = true"
        />
      </div>
    </div>
    <!-- 平台预设 -->
    <div
      v-show="customStyle?.version === 0"
      class="flex flex-col gap-4 p-3 overflow-auto mt-1"
    >
      <div class="flex justify-between items-center">
        <div class="flex items-center">
          <h2 class="text-base">
            <div>{{ $t("trait.artStyleTitle") }}</div>
          </h2>
        </div>
        <button
          :disabled="mixStyle.length < 2"
          class="btn btn-xs rounded btn-primary"
          @click="clickFuseSetting"
        >
          {{ $t("painting.style.mixSetting") }}
        </button>
      </div>
      <div class="grid grid-cols-3 gap-1">
        <div
          v-for="item in genStyle.styleList"
          :key="item.id"
          :class="[
            'rounded overflow-hidden relative w-full aspect-[3/4] border-2',
            mixStyle.findIndex((i) => i.id === item.id) !== -1
              ? 'border-primary'
              : 'border-transparent',
          ]"
          @click="clickPresetItem(item.id)"
        >
          <div>
            <BaseImg :url="usePreviewImg(item.coverImage)" class="w-full" />
            <check-circle-fill-icon
              v-if="mixStyle.findIndex((i) => i.id === item.id) !== -1"
              class="text-primary absolute right-1 bottom-1 icon-xl rounded-full"
            />
          </div>
          <div
            :class="{
              'bg-red-500': mixStyle.findIndex((i) => i.id === item.id) !== -1,
            }"
            class="badge absolute left-1 bottom-1 border-1"
          >
            {{ item.id }}
          </div>
        </div>
      </div>
    </div>

    <div v-if="customStyle?.version !== 0" class="my-2.5 mx-2">
      <InputTag
        v-model:prompt="customStyle!.prompt"
        :show-negative="false"
        :show-upgrade-prompt="false"
      />
    </div>
    <div
      v-show="customStyle?.version !== 0"
      class="flex flex-col gap-4 px-3 overflow-auto"
    >
      <div class="grid grid-cols-3 gap-1">
        <div
          v-for="item in genStyle.customStyleList"
          :key="item.name"
          :class="[
            'rounded overflow-hidden relative w-full aspect-[3/4] border-2',
            customStyle?.prompt.includes(item.name)
              ? 'border-primary'
              : 'border-transparent',
          ]"
          @click="clickCustomItem(item.name)"
        >
          <div>
            <BaseImg :url="usePreviewImg(item.coverImage)" class="w-full" />
            <!-- <check-circle-fill-icon
              v-if="customStyle?.prompt.includes(item.name)"
              class="text-primary absolute right-1 bottom-1 icon-xl rounded-full"
            /> -->
          </div>
          <div
            :class="{
              'bg-primary': customStyle?.prompt.includes(item.name),
            }"
            class="badge badge-sm p-1 badge-ghost absolute left-1 bottom-1 border-0 h-auto sm:badge-base sm:p-2"
          >
            {{ $t(`painting.styles.${item.name}`) }}
          </div>
        </div>
      </div>
    </div>
  </div>

  <VanDialog
    v-if="mixStyle.length === 2"
    v-model:show="showFuseSettingModal"
    :show-confirm-button="false"
    :title="$t(`painting.style.mixSetting`)"
    class="text-my-gray"
    closeOnClickOverlay
  >
    <div class="my-8 mx-4">
      <div class="flex gap-6 items-center">
        <div class="text-xs">
          <img
            :src="usePreviewImg(genStyle.getStyleUrlById(mixStyle[0].id))"
            alt=""
            class="w-[122px]"
          />
          <p class="my-2 text-primary">
            {{ $t("painting.style.styleId") }}: {{ mixStyle[0].id }}
          </p>
          <div class="flex items-center gap-2 text-primary">
            <p>{{ $t("painting.style.consistency") }} :</p>
            <VanField
              v-model.number="mixStyle[0].percent"
              class="flex-1 h-8 leading-8 p-0 bg-transparent border border-my-dark rounded w-11 pl-2.5 red"
              type="number"
              @blur="onLeftChange"
            />
          </div>
        </div>
        <button class="btn btn-primary btn-sm -mt-7" @click="clickSwap">
          <i class="iconfont icon-switch"></i>
        </button>
        <div class="text-xs">
          <img
            :src="usePreviewImg(genStyle.getStyleUrlById(mixStyle[1].id))"
            alt=""
            class="w-[122px]"
          />
          <p class="my-2 text-white">
            {{ $t("painting.style.styleId") }}: {{ mixStyle[1].id }}
          </p>
          <div class="flex items-center gap-2 text-white">
            <p>{{ $t("painting.style.consistency") }} :</p>
            <VanField
              v-model.number="mixStyle[1].percent"
              class="flex-1 h-8 leading-8 p-0 bg-transparent border border-my-dark rounded w-11 pl-2.5 text-my-gray"
              type="number"
              @blur="onRightChange"
            />
          </div>
        </div>
      </div>
      <VanSlider
        v-model="sliderVal"
        :step="10"
        class="mt-4"
        inactive-color="#fff"
        @update:model-value="onChange"
      />

      <div class="text-center mt-8">
        <button class="btn btn-primary btn-sm mx-auto" @click="clickConfirmMix">
          {{ $t("Buttons.confirm") }}
        </button>
      </div>
    </div>
  </VanDialog>

  <VanPopup v-model:show="showModelSelectModal" position="bottom" round>
    <VanPicker
      :columns="option"
      @cancel="showModelSelectModal = false"
      @confirm="onConfirm"
    />
  </VanPopup>
</template>

<style scoped>
:deep(.red .van-field__control) {
  color: hsl(var(--p) / var(--tw-text-opacity));
}
</style>
